import React, { useState, useRef, useEffect } from "react";
import { colors, radius, spacing, elevation } from "../../utils/designSystem";

export function BoxTeaser({ unlocked, onOpen, showHintAfterMs = 8000 }) {
  const [showHint, setShowHint] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const wasUnlockedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (unlocked) {
      if (!wasUnlockedRef.current) {
        setJustUnlocked(true);
        try { navigator.vibrate && navigator.vibrate(80); } catch {}
        setTimeout(() => setJustUnlocked(false), 2500);
      }
      wasUnlockedRef.current = true;
      timerRef.current && clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setShowHint(true), showHintAfterMs);
      return () => clearTimeout(timerRef.current);
    } else {
      wasUnlockedRef.current = false;
      setShowHint(false);
      timerRef.current && clearTimeout(timerRef.current);
    }
  }, [unlocked, showHintAfterMs]);

  return (
    <div className="relative">
      <button
        aria-live="polite"
        onClick={unlocked ? onOpen : undefined}
        aria-label={unlocked ? "Open your prize" : "Prize locked"}
        title={unlocked ? "Open your prize" : "Solve all clues to unlock"}
        className="group relative inline-flex items-center gap-2 text-sm font-semibold transition-all duration-300"
        style={unlocked ? {
          background: `linear-gradient(135deg, ${colors.treasure.gold} 0%, ${colors.treasure.amber} 100%)`,
          color: colors.neutral.black,
          borderRadius: radius.full,
          padding: `${spacing[2]} ${spacing[3]}`,
          boxShadow: elevation.glow.treasure,
        } : {
          background: colors.neutral[200],
          color: colors.neutral[500],
          borderRadius: radius.full,
          padding: `${spacing[2]} ${spacing[3]}`,
          cursor: 'not-allowed',
        }}
        onMouseEnter={(e) => {
          if (unlocked) {
            e.currentTarget.style.filter = 'brightness(1.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (unlocked) {
            e.currentTarget.style.filter = 'brightness(1)';
          }
        }}
      >
        {unlocked && (
          <span className="absolute -inset-1 rounded-full blur-md opacity-70 animate-[ping_1.2s_ease-out_infinite]" />
        )}
        <span className="text-lg leading-none">📦</span>
        <span className={unlocked ? "block" : "hidden sm:block"}>
          {unlocked ? "Open Box" : "Box"}
        </span>

        {unlocked && (
          <span className="absolute -top-1 -right-1 grid h-5 min-w-5 place-items-center rounded-full bg-black px-1 text-[9px] font-bold text-white">NEW</span>
        )}
      </button>

      {unlocked && showHint && (
        <div className="pointer-events-none absolute -right-10 -top-6 animate-bounce-soft text-xl" aria-hidden>
          👉
        </div>
      )}

      {unlocked && justUnlocked && (
        <div
          className="absolute -top-10 left-1/2 -translate-x-1/2 text-xs font-semibold shadow"
          style={{
            background: colors.neutral.black,
            color: colors.neutral.white,
            borderRadius: radius.sm,
            padding: `${spacing[1]} ${spacing[2]}`,
          }}
        >
          Prize unlocked!
        </div>
      )}

      <style>{`
        @keyframes bounce-soft { 0%,100%{ transform: translateY(0); } 50%{ transform: translateY(-6px);} }
        .animate-bounce-soft { animation: bounce-soft 1.2s infinite; }
      `}</style>
    </div>
  );
}
